import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getJobById } from '../../services/public/jobService';
import authService from '../../services/candidate/authService';
import { 
    toTitleCase, 
    cleanLocationString, 
    formatPostedTime, 
    formatContractType, 
    formatSalary, 
    formatExperience, 
    getJobIcon, 
    getJobIconBgColor, 
    getJobIconColor, 
    cleanHtmlContent
} from '../../utils/jobDataTransform';
import { formatRelativeTime } from '../../utils/dateFormatter';
import ApplyEmailModal from '../../components/jobs/ApplyEmailModal';
import { useSavedJobs } from '../../hooks/useSavedJobs';


const JobDetail = () => {
    const { styles, isPublic } = useTheme();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [job, setJob] = useState(null);
    const [similarJobs, setSimilarJobs] = useState([]);
    const [showMobileSticky, setShowMobileSticky] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);

    // Apply email modal state
    const [showApplyEmailModal, setShowApplyEmailModal] = useState(false);
    const [selectedJobForEmail, setSelectedJobForEmail] = useState(null);

    // Auth state
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    
    useEffect(() => {
        const isAuth = authService.isAuthenticated();
        setIsLoggedIn(!!isAuth);
    }, []);

    // Initialize the saved jobs hook
    const { savedJobsIds, toggleSaveJob: toggleSaveJobHook } = useSavedJobs(isLoggedIn);

    // Transform job to UI format
    const transformJobToUIFormat = (job) => {
        //console.log("Transforming job data:", job);
        if (job.timestamp) return job;
        
        return {
            id: job.id,
            title: toTitleCase(job.title) || 'Untitled Position',
            company: job.company || 'Company not specified',
            categoryLabel: job?.category?.label || 'General',
            location: cleanLocationString(job.location?.displayName) || 'Location not specified',
            postedTime: formatRelativeTime(job.createdAt),
            type: formatContractType(job.contractType),
            salary: formatSalary(job.salary),
            experience: formatExperience(job.experience),
            education: job?.education,
            workMode: job?.workMode,
            aiMatch: `AI Match: ${Math.floor(Math.random() * 20) + 80}%`,
            aiMatchColor: "#FFD166",
            description: job.description,
            fullDescription: job.fullDescription,
            skills: job.skills || ['Skills vary'],
            icon: getJobIcon(job.category || ''),
            iconBgColor: getJobIconBgColor(job.category || ''),
            iconColor: getJobIconColor(job.category || ''),
            redirectUrl: job.redirect_url || job.redirectUrl || null,            
            source: job.source || 'hunarpool',
            applyEmail: job.applyEmail,
            workMode: job.workMode || 'On-site',
            originalData: job
        };
    };

    // Fetch job data
    useEffect(() => {
        abortControllerRef.current = new AbortController();
        
        const fetchJobDetails = async () => {
            try {
                setLoading(true);
                setError(null);

                const storedJob = localStorage.getItem(`job_${id}`);
                
                if (storedJob) {
                    const parsedJob = JSON.parse(storedJob);
                    const isFresh = Date.now() - parsedJob.timestamp < 3600000;
                    
                    if (isFresh) {
                        setJob(parsedJob);
                        setSimilarJobs([]);
                        setLoading(false);
                        return;
                    } else {
                        localStorage.removeItem(`job_${id}`);
                    }
                }
                
                const response = await getJobById(id);
                if (response.success) {
                    const transformedJob = transformJobToUIFormat(response.data);
                    setJob(transformedJob);
                    setSimilarJobs(response.similarJobs || []);
                } else {
                    setError('Failed to load job details');
                }
            } catch (err) {
                if (err.code !== 'ERR_CANCELED' && err.name !== 'AbortError' && err.name !== 'CanceledError') {
                    console.error('Error fetching job:', err);
                    setError('Failed to load job details. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchJobDetails();

        return () => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, [id]);

    // Handle scroll for mobile sticky bar
    useEffect(() => {
        let lastScrollTop = 0;
        
        const handleScroll = () => {
            if (window.innerWidth < 1024) {
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                
                if (scrollTop > lastScrollTop) {
                    setShowMobileSticky(false);
                } else {
                    setShowMobileSticky(true);
                }
                
                lastScrollTop = scrollTop;
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Handlers
    const handleToggleSave = () => {
        if (!isLoggedIn) {
            navigate('/candidate/login');
            return;
        }
        toggleSaveJobHook(job);
    };

    const handleApplyNow = (job) => {
        //console.log('Applying to job:', job);
        const { redirectUrl, source, jobId, applyEmail } = job;        
        if (isLoggedIn) {
            if (source === 'hunarpool' && applyEmail) {
                // show the popup of apply email
                setSelectedJobForEmail(job);
                setShowApplyEmailModal(true);
                return;
            }
            else if(source === 'hunarpool' && redirectUrl) {
                // Redirect to external URL for hunarpool jobs with redirect URL 
                window.open(redirectUrl, '_blank', 'noopener,noreferrer');
                return;
            }
            else {
                // Redirect to adzuna redirect URL for external jobs
                window.open(redirectUrl, '_blank', 'noopener,noreferrer');
                return;
            }
        } else {
            navigate('/candidate/login', { 
                state: { 
                    from: location.pathname,
                    jobRedirectUrl: redirectUrl,
                    jobSource: source
                } 
            });
        }
    };

    const handleBackClick = (e) => {
        e.preventDefault();
        navigate(-1);
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen pt-24 pb-16 container mx-auto px-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0096C7] mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading job details...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !job) {
        return (
            <div className="min-h-screen pt-24 pb-16 container mx-auto px-4">
                {/* SEO Settings */}
                <title>{`Job Detail | ${process.env.REACT_APP_NAME}`}</title>

                <div className="text-center">
                    <i className="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                    <h3 className={`text-xl font-bold text-white mb-2`}>Something went wrong</h3>
                    <p className="text-gray-400 mb-6">{error || 'Job not found'}</p>
                    <button 
                        onClick={() => isPublic ? navigate('/jobs') : navigate('/candidate/jobs')}
                        className={`${styles.primaryGradient} ${styles.primaryText} px-6 py-3 rounded-xl hover:shadow-lg`}
                    >
                        Back to Jobs
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* SEO Settings */}
            <title>{`${job?.metaTags?.title || job.title} – Apply for This Job Opportunity | ${process.env.REACT_APP_NAME}`}</title>
            <meta
                name="description"
                content={job?.metaTags?.description || job.description}
            />

            <div className={`${isPublic ? 'container mx-auto px-4 md:px-6 pt-24 pb-24' : ''}`}>
                {/* Back Button */}
                <div className="mb-6">
                    <button 
                        onClick={handleBackClick}
                        className={`text-[#0096C7] hover:text-[#00a8e0] font-medium flex items-center bg-transparent border-none cursor-pointer`}
                    >
                        <i className="fas fa-arrow-left mr-2"></i>
                        Back
                    </button>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main Content */}
                    <div className="lg:w-2/3">
                        {/* Job Header */}
                        <div className={`${styles.cardBg} rounded-2xl p-6 md:p-8 border ${styles.cardBorder} mb-6`}>
                            <div className="flex flex-col">
                                {/* First Row */}
                                <div className="flex items-start gap-4 mb-4">
                                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center flex-shrink-0">
                                        <i className={`fas ${getJobIcon(job.category)} ${styles.iconPrimary} text-2xl md:text-3xl`} style={{ color: getJobIconColor(job.category) }}></i>
                                    </div>
                                    
                                    <div className="flex-1">
                                        <h1 className={`text-lg md:text-3xl font-bold ${styles.text} mb-1 capitalize`}>
                                            {job.title}
                                        </h1>
                                        <div className={`flex items-center ${styles.textSecondary}`}>
                                            <i className="fas fa-building text-gray-500 mr-2 text-sm"></i>
                                            <span className="font-medium">{job.company}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Second Row */}
                                <div className="ml-0 md:ml-20">
                                    {/* Location, Type, Posted Time */}
                                    <div className="flex flex-wrap items-center gap-4 mb-4">
                                        <div className={`flex items-center ${styles.textMuted}`}>
                                            <i className="fas fa-map-marker-alt text-gray-500 mr-2 w-4"></i>
                                            <span className={styles.textSecondary}>{job.location}</span>
                                        </div>
                                        <div className={`flex items-center ${styles.textMuted}`}>
                                            <i className="fas fa-briefcase text-gray-500 mr-2 w-4"></i>
                                            <span className={styles.textSecondary}>{job.type}</span>
                                        </div>
                                        <div className={`flex items-center ${styles.textMuted}`}>
                                            <i className="fas fa-clock text-gray-500 mr-2 w-4"></i>
                                            <span className={styles.textSecondary}>Posted {job.postedTime}</span>
                                        </div>
                                    </div>

                                    {/* Salary and Experience Chips */}
                                    <div className="flex flex-wrap gap-3">
                                        <span className={`${isPublic ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded-lg font-medium inline-flex items-center px-2 py-1 text-sm md:px-4 md:py-2 md:text-md`}>
                                            <i className={`fas ${getJobIcon(job.category)} mr-2`}></i>
                                            {job.categoryLabel}
                                        </span>
                                        <span className={`${isPublic ? 'bg-gray-800' : 'bg-gray-100'} ${isPublic ? 'text-[#06D6A0]' : 'text-green-600'} rounded-lg font-medium inline-flex items-center px-2 py-1 text-sm md:px-4 md:py-2 md:text-md`}>
                                            <i className="fas fa-money-bill-wave mr-2"></i>
                                            {job.salary}
                                        </span>
                                        <span className={`${isPublic ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded-lg font-medium inline-flex items-center px-2 py-1 text-sm md:px-4 md:py-2 md:text-md`}>
                                            <i className="fas fa-chart-line mr-2"></i>
                                            {job.experience}
                                        </span>
                                        {( job.workMode &&
                                        <span className={`${isPublic ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded-lg font-medium inline-flex items-center px-2 py-1 text-sm md:px-4 md:py-2 md:text-md`}>
                                            <i className="fas fa-laptop mr-2"></i>
                                            {job.workMode}
                                        </span>
                                        )}
                                        {( job.education &&
                                        <span className={`${isPublic ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded-lg font-medium inline-flex items-center px-2 py-1 text-sm md:px-4 md:py-2 md:text-md`}>
                                            <i className="fas fa-graduation-cap mr-2"></i>
                                            {job.education}
                                        </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Mobile Action Buttons */}
                            <div className="lg:hidden flex flex-col space-y-4 mt-6">
                                <div className="flex space-x-4">
                                    <button 
                                        onClick={handleToggleSave}
                                        className={`flex-1 border ${isPublic ? 'border-gray-700 text-gray-400 hover:text-[#0096C7] hover:border-[#0096C7]' : 'border-gray-300 text-gray-600 hover:text-primary-600 hover:border-primary-600'} px-4 py-3 rounded-xl flex items-center justify-center transition-all duration-300`}
                                    >
                                        <i className={`${savedJobsIds.includes(job?.id) ? 'fas' : 'far'} fa-heart mr-2`}></i>
                                        {savedJobsIds.includes(job?.id) ? 'Saved' : 'Save'}
                                    </button>

                                    <button 
                                        onClick={() => handleApplyNow(job)}
                                        className="flex-1 bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-semibold px-4 py-3 rounded-xl flex items-center justify-center hover:shadow-lg hover:shadow-[#0096C7]/20 transition-all duration-300"
                                    >
                                        <i className="fas fa-paper-plane mr-2"></i>
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Job Description */}
                        {job.description && (
                            <div className={`${styles.cardBg} rounded-2xl p-6 md:p-8 border ${styles.cardBorder} mb-6`}>
                                <h2 className={`text-lg md:text-2xl font-bold ${styles.text} mb-6 pb-4 border-b ${styles.border}`}>
                                    {job.source != 'hunarpool' ? 'Job Description' : 'About Company'}
                                </h2>
                                
                                <div 
                                    className={`prose max-w-none ${isPublic ? 'prose-invert text-gray-300' : 'text-gray-700'}`}
                                    dangerouslySetInnerHTML={{ 
                                        __html: cleanHtmlContent(job.description) 
                                    }}
                                />
                            </div>
                        )}

                        {/* Full Description */}
                        {job.fullDescription && (
                            <div className={`${styles.cardBg} rounded-2xl p-6 md:p-8 border ${styles.cardBorder} mb-6`}>
                                <h2 className={`text-lg md:text-2xl font-bold ${styles.text} mb-6 pb-4 border-b ${styles.border}`}>
                                    Job Description
                                </h2>
                                
                                <div 
                                    className={`prose max-w-none ${isPublic ? 'prose-invert text-gray-300' : 'text-gray-700'}`}
                                    dangerouslySetInnerHTML={{ 
                                        __html: cleanHtmlContent(job.fullDescription) 
                                    }}
                                />
                            </div>
                        )}

                        {/* Education */}
                        {job.education && (
                            <div className={`${styles.cardBg} rounded-2xl p-6 md:p-8 border ${styles.cardBorder} mb-6`}>
                                <h2 className={`text-lg md:text-2xl font-bold ${styles.text} mb-4`}>Education Requirements</h2>
                                <p className={isPublic ? 'text-gray-300' : 'text-gray-700'}>{job.education}</p>
                            </div>
                        )}

                        {/* Skills */}
                        {job.skills && job.skills.length > 0 && (
                            <div className={`${styles.cardBg} rounded-2xl p-6 md:p-8 border ${styles.cardBorder} mb-6`}>
                                <h2 className={`text-lg md:text-2xl font-bold ${styles.text} mb-6`}>Required Skills</h2>
                                <div className="flex flex-wrap gap-3">
                                    {job.skills.map((skill, index) => (
                                        <span key={index} className={`${isPublic ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800'} px-4 py-2 rounded-lg font-medium`}>
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:w-1/3">
                        {/* Apply Card - Desktop */}
                        <div className={`hidden lg:block top-24 ${styles.cardBg} rounded-2xl p-6 border ${styles.cardBorder} mb-6 z-10`}>
                            <div className="text-center mb-6">
                                <div className={`w-20 h-20 rounded-full ${isPublic ? 'bg-gradient-to-br from-gray-800 to-gray-700' : 'bg-gradient-to-br from-gray-100 to-gray-200'} flex items-center justify-center mx-auto mb-4`}>
                                    <i className={`fas fa-briefcase ${styles.iconPrimary} text-3xl`}></i>
                                </div>
                                <h3 className={`text-xl font-bold ${styles.text} mb-2`}>Ready to Apply?</h3>                                
                            </div>
                            
                            <div className="space-y-4">
                                <button 
                                    onClick={() => handleApplyNow(job)}
                                    className={`w-full ${styles.primaryGradient} ${styles.primaryHover} ${styles.primaryText} font-semibold px-6 py-4 rounded-xl flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20`}
                                >
                                    <i className="fas fa-paper-plane mr-3"></i>
                                    Apply Now
                                </button>                                
                                
                                <button 
                                    onClick={handleToggleSave}
                                    className={`w-full border font-semibold px-6 py-4 rounded-xl flex items-center justify-center transition-all duration-300 ${
                                        savedJobsIds.includes(job?.id)
                                            ? `${isPublic ? 'border-[#0096C7] text-[#0096C7]' : 'border-primary-600 text-primary-600'}` 
                                            : `${isPublic ? 'border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-[#0096C7]' : 'border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-primary-600'}`
                                    }`}
                                >
                                    <i className={`${savedJobsIds.includes(job?.id) ? 'fas' : 'far'} fa-heart mr-3`}></i>
                                    {savedJobsIds.includes(job?.id) ? 'Saved Job' : 'Save Job'}
                                </button>
                            </div>

                            {/* Quick tips */}
                            <div className={`mt-6 pt-6 border-t ${styles.border}`}>
                                <h4 className={`font-semibold ${styles.text} mb-4`}>Application Tips</h4>
                                <ul className={`space-y-3 text-sm ${styles.textMuted}`}>
                                    <li className="flex items-start">
                                        <i className="fas fa-check text-[#06D6A0] mt-1 mr-3"></i>
                                        <span>Highlight relevant skills and experience in resume</span>
                                    </li>
                                    <li className="flex items-start">
                                        <i className="fas fa-check text-[#06D6A0] mt-1 mr-3"></i>
                                        <span>Add portfolio or work samples (if available)</span>
                                    </li>
                                    <li className="flex items-start">
                                        <i className="fas fa-check text-[#06D6A0] mt-1 mr-3"></i>
                                        <span>Showcase key projects or achievements</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Similar Jobs */}
                        {similarJobs.length > 0 && (
                            <div className={`${styles.cardBg} rounded-2xl p-6 border ${styles.cardBorder}`}>
                                <h3 className={`text-lg md:text-2xl font-bold ${styles.text} mb-6`}>Similar Jobs</h3>
                                
                                <div className="space-y-5">
                                    {similarJobs.map(similarJob => (
                                        <Link 
                                            key={similarJob.id}
                                            to={isPublic ? `/jobs/${similarJob.id}` : `/candidate/jobs/${similarJob.id}`}
                                            state={{ fromJobs: true }}
                                            className={`block p-4 border ${isPublic ? 'border-gray-800 hover:border-gray-700' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'} rounded-xl transition-all duration-300`}
                                        >
                                            <div className="flex items-start">
                                                <div className={`w-12 h-12 rounded-xl ${getJobIconBgColor(similarJob.category)} flex items-center justify-center flex-shrink-0 mr-4`}>
                                                    <i className={`fas ${getJobIcon(similarJob.category)} text-1xl`} style={{ color: getJobIconColor(similarJob.category) }}></i>
                                                </div>

                                                <div>
                                                    <div className={`font-bold ${styles.text} mb-1`}>{toTitleCase(similarJob.title)}</div>
                                                    <div className={`${styles.textMuted} text-sm mb-2`}>
                                                        {similarJob.company} • {cleanLocationString(similarJob.location?.display_name)}
                                                    </div>
                                                    <div className="flex items-center text-sm">
                                                        <span className={`${isPublic ? 'text-[#06D6A0]' : 'text-green-600'} font-medium mr-3`}>
                                                            {formatSalary(similarJob.salary)}
                                                        </span>                                                    
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                                
                                <Link 
                                    to="/jobs" 
                                    className={`block mt-6 text-center ${styles.iconPrimary} hover:${isPublic ? 'text-[#00a8e0]' : 'text-primary-700'} font-medium`}
                                >
                                    View more similar jobs <i className="fas fa-arrow-right ml-2"></i>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Sticky Apply Bar - Mobile */}
            <div className={`lg:hidden fixed bottom-0 left-0 right-0 ${isPublic ? 'bg-[#222629] border-gray-800' : 'bg-white border-gray-200'} p-4 border-t sticky-apply z-40 transition-transform duration-300 ${
                showMobileSticky ? 'translate-y-0' : 'translate-y-full'
            }`}>
                <div className="container mx-auto">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className={`font-bold text-white truncate max-w-[150px]`}>{job.title}</div>
                            <div className={`text-gray-300 text-sm`}>
                                {job.company}
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button 
                                onClick={handleToggleSave}
                                className={`p-3 transition-all duration-300 ${
                                    savedJobsIds.includes(job?.id) ? styles.iconPrimary : styles.textMuted
                                } hover:${styles.iconPrimary}`}
                            >
                                <i className={`${savedJobsIds.includes(job?.id) ? 'fas' : 'far'} fa-heart text-xl`}></i>
                            </button>
                            <button 
                                onClick={() => handleApplyNow(job)}
                                className="bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-semibold px-6 py-3 rounded-xl flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20"
                            >
                                Apply Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Apply Email Modal */}
            <ApplyEmailModal 
                isOpen={showApplyEmailModal}
                onClose={() => {
                    setShowApplyEmailModal(false);
                    setSelectedJobForEmail(null);
                }}
                job={selectedJobForEmail || {}}
            />
        </>
    );
};

export default JobDetail;